import {
    ColumnOrderState,
    ColumnSizingInfoState,
    ColumnSizingState,
    OnChangeFn,
    PaginationState,
    SortingState,
    VisibilityState,
    functionalUpdate,
} from '@tanstack/react-table'
import { useRef, useState } from 'react'
import { hiddenToVisibility, tableStateToQueryParams, visibilityToHidden } from '../utils/utils'
import useTableSettings, { DEFAULT_COLUMN_SORT } from './useTableSettings'

interface UseTableStateConfig {
    tableKey?: string // drives persistence: seeds state from userSettings on init; guards onChange callbacks from dispatching when absent
    initialPage?: number // 1-based; for back-nav via history.location.state.page
    initialPageSize?: number
}

// Pass tableKey for tables with user-configurable state (sort, column visibility, order, sizing).
// Omit it for static display tables — local state still works, nothing is persisted.
const useTableState = (props: UseTableStateConfig = {}) => {
    const { tableKey, initialPage, initialPageSize = 20 } = props

    const {
        storedSort,
        updateSort,
        storedHiddenColumns,
        updateVisibility,
        storedColumnOrder,
        updateColumnOrder,
        storedColumnSizing,
        updateColumnSizing,
        storedPageSize,
        updatePageSize,
    } = useTableSettings(tableKey ?? '')

    const defaultSort: SortingState = tableKey ? (DEFAULT_COLUMN_SORT[tableKey] ?? DEFAULT_COLUMN_SORT.default) : []

    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: initialPage ? initialPage - 1 : 0,
        pageSize: storedPageSize ?? initialPageSize,
    })
    const [sorting, setSorting] = useState<SortingState>(storedSort ?? defaultSort)
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(hiddenToVisibility(storedHiddenColumns ?? []))
    const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(storedColumnOrder ?? [])
    const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(storedColumnSizing ?? {})
    const columnSizingRef = useRef(columnSizing)
    const columnSizingInfoRef = useRef<ColumnSizingInfoState>({
        columnSizingStart: [],
        deltaOffset: null,
        deltaPercentage: null,
        isResizingColumn: false,
        startOffset: null,
        startSize: null,
    })

    // updates both pageIndex (local state) and pageSize (local state + userSettings)
    const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
        setPagination((prev) => {
            const newPagination = functionalUpdate(updater, prev)

            // persist only pageSize to userSettings, no pageIndex (ephemeral)
            if (tableKey && newPagination.pageSize !== prev.pageSize) updatePageSize(newPagination.pageSize)
            return newPagination
        })
    }

    const onSortingChange: OnChangeFn<SortingState> = (updater) => {
        const newSort = functionalUpdate(updater, sorting)
        setSorting(newSort)
        if (tableKey) updateSort(newSort)
    }

    const onColumnVisibilityChange: OnChangeFn<VisibilityState> = (updater) => {
        const newVisibility = functionalUpdate(updater, columnVisibility)
        setColumnVisibility(newVisibility)
        if (tableKey) updateVisibility(visibilityToHidden(newVisibility))
    }

    const onColumnOrderChange: OnChangeFn<ColumnOrderState> = (updater) => {
        const newOrder = functionalUpdate(updater, columnOrder)
        setColumnOrder(newOrder)
        if (tableKey) updateColumnOrder(newOrder)
    }

    // onColumnSizingChange - reports the widths (what the sizes are)
    // updateColumnSizing is intentionally not called here — 'onChange' fires on every drag pixel, so persisting here would hammer the backend. 
    // The final value is saved once via onColumnSizingInfoChange below.
    // If performance issues occured, consider using 'onEnd' mode instead, or take a look at
    // https://tanstack.com/table/latest/docs/guide/column-sizing#advanced-column-resizing-performance
    const onColumnSizingChange: OnChangeFn<ColumnSizingState> = (updater) => {
        const newSizing = functionalUpdate(updater, columnSizingRef.current)
        columnSizingRef.current = newSizing
        setColumnSizing(newSizing)
    }

    // onColumnSizingInfoChange - reports the drag gesture (whether a resize is happening right now)
    // isResizingColumn flips from the column id back to false the instant a resize ends
    const onColumnSizingInfoChange: OnChangeFn<ColumnSizingInfoState> = (updater) => {
        const prevInfo = columnSizingInfoRef.current
        const newInfo = functionalUpdate(updater, prevInfo)
        columnSizingInfoRef.current = newInfo

        if (tableKey && prevInfo.isResizingColumn && !newInfo.isResizingColumn) {
            updateColumnSizing(columnSizingRef.current)
        }
    }

    const params = tableStateToQueryParams({ pagination, sorting })

    return {
        tableKey,
        pagination,
        onPaginationChange,
        sorting,
        onSortingChange,
        columnVisibility,
        onColumnVisibilityChange,
        columnOrder,
        onColumnOrderChange,
        columnSizing,
        onColumnSizingChange,
        onColumnSizingInfoChange,
        updateColumnSizing: (sizing: ColumnSizingState) => {
            if (tableKey) updateColumnSizing(sizing)
        },
        params,
    }
}

export default useTableState
